import React, { useState, useRef } from 'react';
import { Popover, Tooltip } from '@shopify/polaris';
import ChoiceListFilter from './filters/ChoiceListFilter';
import DateRangeFilter from './filters/DateRangeFilter';

/**
 * HeaderCell — renders a single <th> for the TanStack table.
 *
 * Props:
 *   column          : TanStack column object (has id, getCanSort, getIsSorted, toggleSorting)
 *   label           : string
 *   sortable        : bool
 *   filterType      : null | 'choice' | 'date'
 *   filterProps     : object — passed to the filter component
 *   isStickyCb      : bool — sticky checkbox column
 *   isStickyOrder   : bool — sticky order# column
 *   dragHandlers    : { onDragStart, onDragOver, onDrop, onDragEnd }
 *   isDragging      : bool
 *   isDragOver      : bool
 *   pinnedCols      : { [colId]: 'left'|'right' }
 *   setPin          : (colId, side) => void
 *   openPinMenuId   : string|null
 *   setOpenPinMenuId: (id|null) => void
 *   pinStyle        : object|undefined — inline style for sticky positioning
 *   pinClass        : string — extra CSS classes for freeze-line borders
 */
export default function HeaderCell({
  column,
  label,
  tooltip = null,
  sortable = false,
  filterType = null,
  filterProps = {},
  isStickyCb = false,
  isStickyOrder = false,
  colId = undefined,
  dragHandlers = {},
  isDragging = false,
  isDragOver = false,
  openFunnelId,
  setOpenFunnelId,
  pinnedCols = {},
  setPin = () => {},
  openPinMenuId = null,
  setOpenPinMenuId = () => {},
  pinStyle = undefined,
  pinClass = '',
}) {
  // Filter-popover open state lifted to the parent — only one funnel popover
  // open at a time. Falls back to local state if parent doesn't pass props
  // (lets HeaderCell stay usable in isolation).
  const [localOpen, setLocalOpen] = useState(false);
  const filterOpen = setOpenFunnelId
    ? (openFunnelId === colId)
    : localOpen;
  const setFilterOpen = setOpenFunnelId
    ? (val) => setOpenFunnelId(typeof val === 'function' ? (val(filterOpen) ? colId : null) : (val ? colId : null))
    : setLocalOpen;
  const funnelRef = useRef(null);

  const sorted = column ? column.getIsSorted() : false;
  const isFilterActive = filterProps.isActive;

  // Pin menu state — lifted to parent so only one is open at a time
  const pinMenuOpen = openPinMenuId === colId;
  const setPinMenuOpen = (val) => {
    const next = typeof val === 'function' ? val(pinMenuOpen) : val;
    setOpenPinMenuId(next ? colId : null);
  };
  const pinSide = pinnedCols[colId]; // 'left' | 'right' | undefined

  // Whether this column can be pinned (checkbox + name are always-sticky; no pin menu)
  const canPin = colId !== 'checkbox' && colId !== 'name' && !isStickyCb && !isStickyOrder;

  const handleSortClick = (e) => {
    if (!sortable || !column) return;
    column.toggleSorting(undefined, false);
  };

  const handleFunnelClick = (e) => {
    e.stopPropagation();
    setFilterOpen(v => !v);
  };

  const thClass = [
    'ots-th',
    isStickyCb ? 'ots-th--sticky-cb' : '',
    isStickyOrder ? 'ots-th--sticky-order' : '',
    isDragging ? 'ots-th--dragging' : '',
    isDragOver ? 'ots-th--drag-over' : '',
    pinClass,
  ].filter(Boolean).join(' ');

  if (isStickyCb) {
    return (
      <th className={thClass} data-colid={colId}>
        <div className="ots-cb-th">
          {filterProps.checkboxEl}
        </div>
      </th>
    );
  }

  const funnelBtn = filterType ? (
    <button
      ref={funnelRef}
      type="button"
      className={`ots-funnel-btn${filterOpen || isFilterActive ? ' ots-funnel-btn--active' : ''}`}
      onClick={handleFunnelClick}
      title="Filter"
      aria-label={`Filter ${label}`}
    >
      {/* AG-Grid-style narrowing horizontal lines filter icon */}
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <line x1="2"  y1="4"  x2="14" y2="4"  />
        <line x1="4"  y1="8"  x2="12" y2="8"  />
        <line x1="6"  y1="12" x2="10" y2="12" />
      </svg>
      {isFilterActive && <span className="ots-funnel-dot" />}
    </button>
  ) : null;

  // Pin menu button + Polaris Popover (only on pinnable columns)
  const pinMenuBtn = canPin ? (
    <Popover
      active={pinMenuOpen}
      activator={
        <button
          type="button"
          className={`ots-pinmenu-btn${pinSide ? ' ots-pinmenu-btn--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); setPinMenuOpen(v => !v); }}
          title="Column options"
        >
          {/* Vertical 3-dot (⋮) */}
          <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="3"  r="1.5" />
            <circle cx="8" cy="8"  r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </button>
      }
      onClose={() => setPinMenuOpen(false)}
      preferredAlignment="left"
    >
      <div className="ots-pinmenu-panel" onClick={(e) => e.stopPropagation()}>
        <label
          className={`ots-pinmenu-row${pinSide === 'right' ? ' ots-pinmenu-row--disabled' : ''}`}
        >
          <input
            type="checkbox"
            checked={pinSide === 'left'}
            disabled={pinSide === 'right'}
            onChange={(e) => {
              setPin(colId, e.target.checked ? 'left' : null);
              setPinMenuOpen(false);
            }}
          />
          <span>Pin Left</span>
        </label>
        <label
          className={`ots-pinmenu-row${pinSide === 'left' ? ' ots-pinmenu-row--disabled' : ''}`}
        >
          <input
            type="checkbox"
            checked={pinSide === 'right'}
            disabled={pinSide === 'left'}
            onChange={(e) => {
              setPin(colId, e.target.checked ? 'right' : null);
              setPinMenuOpen(false);
            }}
          />
          <span>Pin Right</span>
        </label>
      </div>
    </Popover>
  ) : null;

  // Sort indicator: ↕ inactive, ↑ asc, ↓ desc.
  const sortIndicator = sortable ? (
    sorted === 'asc'
      ? <span className="ots-sort-icon">↑</span>
      : sorted === 'desc'
        ? <span className="ots-sort-icon">↓</span>
        : <span className="ots-sort-icon--inactive">↕</span>
  ) : null;

  // Render the appropriate filter popover
  let filterPopover = null;
  if (filterType === 'choice') {
    const activatorEl = funnelBtn;
    filterPopover = (
      <ChoiceListFilter
        active={filterOpen}
        activator={activatorEl}
        onClose={() => setFilterOpen(false)}
        {...filterProps}
      />
    );
  } else if (filterType === 'date') {
    const activatorEl = funnelBtn;
    filterPopover = (
      <DateRangeFilter
        active={filterOpen}
        activator={activatorEl}
        onClose={() => setFilterOpen(false)}
        {...filterProps}
      />
    );
  }

  return (
    <th
      className={thClass}
      style={pinStyle}
      draggable={!isStickyCb && !isStickyOrder}
      onDragStart={dragHandlers.onDragStart}
      onDragOver={dragHandlers.onDragOver}
      onDrop={dragHandlers.onDrop}
      onDragEnd={dragHandlers.onDragEnd}
      data-colid={colId}
      data-pinned={pinSide || undefined}
    >
      <div className="ots-th-inner">
        {sortable ? (
          <div className="ots-th-sort" onClick={handleSortClick} title={tooltip ? undefined : `Sort by ${label}`}>
            {tooltip ? (
              <Tooltip content={tooltip} dismissOnMouseOut preferredPosition="below">
                <span className="ots-th-label" style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{label}</span>
              </Tooltip>
            ) : (
              <span className="ots-th-label">{label}</span>
            )}
            {sortIndicator}
          </div>
        ) : tooltip ? (
          <Tooltip content={tooltip} dismissOnMouseOut preferredPosition="below">
            <span className="ots-th-label" style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{label}</span>
          </Tooltip>
        ) : (
          <span className="ots-th-label">{label}</span>
        )}

        {/* Icon group: ⋮ pin menu (left) + filter icon (right) */}
        {canPin && pinMenuBtn}
        {filterType ? filterPopover : null}
      </div>
    </th>
  );
}
